import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import PublicationDetailSheet from "@/components/PublicationDetailSheet";
import { OnboardingChecklist } from "@/components/OnboardingChecklist";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import {
  MousePointerClick, Users2, TrendingUp, Megaphone, Loader2, CheckCircle2, XCircle, Link2, ClipboardCheck, ChevronRight,
} from "lucide-react";

function StatCard({ label, value, icon: Icon, testid }) {
  return (
    <Card className="rounded-sm border-[#E4E4E7] shadow-none p-6" data-testid={testid}>
      <div className="flex items-start justify-between">
        <span className="text-xs uppercase tracking-wider text-[#71717A] font-medium">{label}</span>
        <Icon className="h-4 w-4 text-[#0055FF]" strokeWidth={2} />
      </div>
      <div className="mt-4 font-heading text-3xl font-extrabold text-[#09090B]">{value}</div>
    </Card>
  );
}

function PendingManualBanner() {
  const { activeTenantId } = useAuth();
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!activeTenantId) return;
    api.get("/social/publications/pending-manual-count").then((r) => setCount(r.data.count)).catch(() => {});
  }, [activeTenantId]);

  if (count === 0) return null;

  return (
    <Card
      className="rounded-sm border-[#8ED500]/40 bg-[#8ED500]/[0.06] shadow-none p-4 flex items-center justify-between flex-wrap gap-3"
      data-testid="pending-manual-banner"
    >
      <div className="flex items-center gap-2 text-sm text-[#3D5F00]">
        <ClipboardCheck className="h-4 w-4 shrink-0" />
        Tenés {count} {count > 1 ? "publicaciones" : "publicación"} esperando que las publiques vos.
      </div>
      <Button asChild size="sm" variant="outline" className="rounded-sm shrink-0" data-testid="pending-manual-banner-button">
        <Link to="/dashboard/publications">Ver publicaciones <ChevronRight className="h-3.5 w-3.5 ml-1" /></Link>
      </Button>
    </Card>
  );
}

const STATUS_LABEL = { new: "Nuevo", contacted: "Contactado", qualified: "Calificado", won: "Ganado", lost: "Perdido" };

export default function DashboardHome() {
  const { activeTenantId, activeTenant, user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailPubId, setDetailPubId] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  useEffect(() => {
    if (!activeTenantId) return;
    setLoading(true);
    api.get("/metrics/dashboard").then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [activeTenantId]);

  const openPub = (id) => { setDetailPubId(id); setDetailOpen(true); };

  return (
    <div className="space-y-8" data-testid="dashboard-home">
      <div>
        <h2 className="font-heading text-2xl font-bold text-[#09090B]">Hola, {user?.name?.split(" ")[0]}</h2>
        <p className="mt-1 text-sm text-[#71717A]">
          Resumen de <span className="font-medium text-[#09090B]">{activeTenant?.name}</span>
        </p>
      </div>

      <OnboardingChecklist />
      <PendingManualBanner />

      {loading || !data ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#0055FF]" /></div>
      ) : data.scope === "sales" ? (
        <SalesDashboard data={data} />
      ) : (
        <CompanyDashboard data={data} onOpenPub={openPub} />
      )}

      <PublicationDetailSheet publicationId={detailPubId} open={detailOpen} onOpenChange={setDetailOpen} />
    </div>
  );
}

function CompanyDashboard({ data, onOpenPub }) {
  const platformData = Object.entries(data.leads_by_platform).map(([platform, count]) => ({ platform, count }));

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Publicaciones (mes)" value={data.publications_month} icon={Megaphone} testid="stat-publications-month" />
        <StatCard label="Clics (mes)" value={data.clicks_month} icon={MousePointerClick} testid="stat-clicks-month" />
        <StatCard label="Leads (mes)" value={data.leads_month} icon={Users2} testid="stat-leads-month" />
        <StatCard label="Conversión clics→leads" value={`${(data.conversion_rate * 100).toFixed(1)}%`} icon={TrendingUp} testid="stat-conversion" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="rounded-sm border-[#E4E4E7] shadow-none p-6" data-testid="leads-by-platform-chart">
          <h3 className="font-heading font-bold text-sm text-[#09090B] mb-4">Leads por plataforma</h3>
          {platformData.length === 0 ? (
            <p className="text-sm text-[#A1A1AA]">Sin leads este mes todavía.</p>
          ) : (
            <div className="h-52">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={platformData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
                  <XAxis dataKey="platform" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#0055FF" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>

        <Card className="rounded-sm border-[#E4E4E7] shadow-none p-6" data-testid="leads-by-salesperson-table">
          <h3 className="font-heading font-bold text-sm text-[#09090B] mb-4">Leads por vendedor / territorio</h3>
          {data.leads_by_salesperson.length === 0 ? (
            <p className="text-sm text-[#A1A1AA]">Sin leads este mes todavía.</p>
          ) : (
            <div className="space-y-2">
              {data.leads_by_salesperson.map((row) => (
                <div key={row.user_id} className="flex items-center justify-between text-sm border-b border-[#F4F4F5] pb-2 last:border-0">
                  <span className={row.user_id === "unassigned" ? "text-[#92400E]" : "text-[#09090B]"}>{row.name}</span>
                  <Badge variant="outline" className="rounded-sm">{row.count}</Badge>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <PublicationRankCard title="Top publicaciones por clics" rows={data.top_publications_by_clicks} metric="click_count" onOpenPub={onOpenPub} testid="top-publications-by-clicks" />
        <PublicationRankCard title="Top publicaciones por leads" rows={data.top_publications_by_leads} metric="leads_count" onOpenPub={onOpenPub} testid="top-publications-by-leads" />
      </div>

      <NetworkMetricsSummaryCard byPlatform={data.network_metrics_by_platform} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="rounded-sm border-[#E4E4E7] shadow-none p-6" data-testid="latest-publications-card">
          <h3 className="font-heading font-bold text-sm text-[#09090B] mb-4">Últimas publicaciones</h3>
          {data.latest_publications.length === 0 ? (
            <p className="text-sm text-[#A1A1AA]">Sin publicaciones todavía.</p>
          ) : (
            <div className="space-y-2">
              {data.latest_publications.map((p) => (
                <button key={p.id} onClick={() => onOpenPub(p.id)} className="w-full flex items-center justify-between text-sm border-b border-[#F4F4F5] pb-2 last:border-0 text-left" data-testid={`latest-pub-${p.id}`}>
                  <span className="truncate max-w-[60%] text-[#09090B]">{p.text}</span>
                  <Badge variant="outline" className="rounded-sm">{p.status}</Badge>
                </button>
              ))}
            </div>
          )}
        </Card>

        <Card className="rounded-sm border-[#E4E4E7] shadow-none p-6" data-testid="accounts-semaphore-card">
          <h3 className="font-heading font-bold text-sm text-[#09090B] mb-4">Cuentas conectadas</h3>
          {data.accounts_summary.length === 0 ? (
            <p className="text-sm text-[#A1A1AA]">Sin cuentas conectadas.</p>
          ) : (
            <div className="space-y-2">
              {data.accounts_summary.map((a) => (
                <div key={a.id} className="flex items-center justify-between text-sm border-b border-[#F4F4F5] pb-2 last:border-0">
                  <span className="capitalize text-[#09090B]">{a.display_name}</span>
                  {a.connection_status === "connected" ? (
                    <span className="flex items-center gap-1 text-[#10B981] text-xs"><CheckCircle2 className="h-3.5 w-3.5" /> conectada</span>
                  ) : (
                    <span className="flex items-center gap-1 text-[#EF4444] text-xs"><XCircle className="h-3.5 w-3.5" /> desconectada</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

const NETWORK_METRIC_LABELS = {
  likes: "Me gusta", comments: "Comentarios", impressions: "Impresiones", reach: "Alcance",
  views: "Vistas", replies: "Respuestas", saves: "Guardados", clicks: "Clics (red)", shares: "Compartidos",
  retweet_count: "Retweets", reply_count: "Respuestas", like_count: "Me gusta", quote_count: "Citas",
  impression_count: "Impresiones", bookmark_count: "Guardados",
};

function NetworkMetricsSummaryCard({ byPlatform }) {
  const platforms = Object.entries(byPlatform || {});
  return (
    <Card className="rounded-sm border-[#E4E4E7] shadow-none p-6" data-testid="network-metrics-summary-card">
      <h3 className="font-heading font-bold text-sm text-[#09090B] mb-1">Según la red</h3>
      <p className="text-xs text-[#71717A] mb-4">
        Impresiones/me gusta que cada plataforma reporta -- distinto de clics y leads (arriba), que son nuestros.
        No se suman entre redes: cada una mide distinto.
      </p>
      {platforms.length === 0 ? (
        <p className="text-sm text-[#A1A1AA]">Todavía no hay métricas de red disponibles.</p>
      ) : (
        <div className="space-y-3">
          {platforms.map(([platform, metrics]) => (
            <div key={platform} className="border-b border-[#F4F4F5] pb-2 last:border-0" data-testid={`network-metrics-platform-${platform}`}>
              <p className="text-xs font-medium capitalize text-[#09090B] mb-1.5">{platform}</p>
              <div className="flex flex-wrap gap-2">
                {Object.entries(metrics).map(([key, value]) => (
                  <span key={key} className="text-xs px-2 py-1 rounded-sm bg-[#F4F4F5] text-[#3F3F46]">
                    {NETWORK_METRIC_LABELS[key] || key}: {value}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function PublicationRankCard({ title, rows, metric, onOpenPub, testid }) {
  return (
    <Card className="rounded-sm border-[#E4E4E7] shadow-none p-6" data-testid={testid}>
      <h3 className="font-heading font-bold text-sm text-[#09090B] mb-4">{title}</h3>
      {rows.length === 0 ? (
        <p className="text-sm text-[#A1A1AA]">Todavía no hay datos suficientes.</p>
      ) : (
        <div className="space-y-2">
          {rows.map((p) => (
            <button key={p.id} onClick={() => onOpenPub(p.id)} className="w-full flex items-center justify-between text-sm border-b border-[#F4F4F5] pb-2 last:border-0 text-left">
              <span className="truncate max-w-[70%] text-[#09090B]">{p.text}</span>
              <Badge className="bg-[#0055FF]/10 text-[#0055FF] border-0 rounded-sm">{p[metric]}</Badge>
            </button>
          ))}
        </div>
      )}
    </Card>
  );
}

function SalesDashboard({ data }) {
  return (
    <div className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard label="Mis clics" value={data.clicks_total} icon={MousePointerClick} testid="stat-my-clicks" />
        <StatCard label="Mis leads" value={data.leads_total} icon={Users2} testid="stat-my-leads" />
        <StatCard label="Mis links" value={data.links.length} icon={Link2} testid="stat-my-links" />
      </div>

      <Card className="rounded-sm border-[#E4E4E7] shadow-none p-6" data-testid="my-leads-by-status">
        <h3 className="font-heading font-bold text-sm text-[#09090B] mb-4">Mis leads por estado</h3>
        {Object.keys(data.leads_by_status).length === 0 ? (
          <p className="text-sm text-[#A1A1AA]">Sin leads todavía.</p>
        ) : (
          <div className="flex gap-3 flex-wrap">
            {Object.entries(data.leads_by_status).map(([status, count]) => (
              <Badge key={status} variant="outline" className="rounded-sm">{STATUS_LABEL[status] || status}: {count}</Badge>
            ))}
          </div>
        )}
      </Card>

      <Card className="rounded-sm border-[#E4E4E7] shadow-none p-6" data-testid="my-links-card">
        <h3 className="font-heading font-bold text-sm text-[#09090B] mb-4">Mis links</h3>
        {data.links.length === 0 ? (
          <p className="text-sm text-[#A1A1AA]">Todavía no generaste ningún link personalizado.</p>
        ) : (
          <div className="space-y-2">
            {data.links.map((l) => (
              <div key={l.code} className="flex items-center justify-between text-sm border-b border-[#F4F4F5] pb-2 last:border-0">
                <span className="font-mono text-xs text-[#71717A]">{l.code}</span>
                <Badge variant="outline" className="rounded-sm">{l.click_count} clics</Badge>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
